/**
 * Domain type aliases for improved readability at app boundaries
 * 
 * These aliases provide semantic meaning to primitive types without
 * modifying the auto-generated Supabase types.
 * 
 * Usage:
 * - Use at API boundaries, function parameters, and component props
 * - Do NOT use inside src/integrations/supabase/types.ts
 */

/** UUID string identifier (e.g., user_id, order_id, book_id) */
export type UUID = string;

/** ISO 8601 timestamp string (e.g., created_at, updated_at) */
export type Timestamp = string;

/** Monetary value in smallest currency unit (e.g., price, amount) */
export type Money = number;

/** Email address string */
export type Email = string;

/** URL string (e.g., image URLs, external links) */
export type URLString = string;

/** Phone number string */
export type PhoneNumber = string;

/** PIN code / postal code string */
export type PinCode = string;

/** JSON data stored in JSONB columns */
export type JsonData = Record<string, unknown>;

/** App roles enum values — re-exported from generated Supabase types to keep a single source of truth */
export type { AppRole } from '@/types/roles';
import type { AppRole } from '@/types/roles';

/** App role constants for runtime access */
export const APP_ROLES = {
  BUYER: 'buyer',
  SELLER: 'seller',
  ADMIN: 'admin',
} as const satisfies Record<string, AppRole>;

/** Order status values */
export type OrderStatus =
  | 'pending'
  | 'confirmed'
  | 'processing'
  | 'ready_for_pickup'
  | 'shipped'
  | 'delivered'
  | 'cancelled';

/** Order status constants for runtime access */
export const ORDER_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  PROCESSING: 'processing',
  READY_FOR_PICKUP: 'ready_for_pickup',
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
} as const satisfies Record<string, OrderStatus>;

/** Payment status values */
export type PaymentStatus = 'pending' | 'completed' | 'failed' | 'refunded';

/** Payment status constants for runtime access */
export const PAYMENT_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  FAILED: 'failed',
  REFUNDED: 'refunded',
} as const satisfies Record<string, PaymentStatus>;

/** Order payment method values */
export type OrderPaymentMethod = 'razorpay' | 'cod';

/** Order payment method constants */
export const ORDER_PAYMENT_METHOD = {
  RAZORPAY: 'razorpay',
  COD: 'cod',
} as const satisfies Record<string, OrderPaymentMethod>;

/** Seller payout method values */
export type SellerPayoutMethod = 'bank' | 'upi';

/** Seller payout method constants */
export const SELLER_PAYOUT_METHOD = {
  BANK: 'bank',
  UPI: 'upi',
} as const satisfies Record<string, SellerPayoutMethod>;

/** Settlement status values (matches values actually written by DB triggers / edge functions). */
export type SettlementStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'rejected'
  | 'on_hold';

/** Settlement status constants */
export const SETTLEMENT_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  REJECTED: 'rejected',
  ON_HOLD: 'on_hold',
} as const satisfies Record<string, SettlementStatus>;

/** Order item status values */
export type ItemStatus = 'confirmed' | 'cancelled' | 'refunded';

/** Order item status constants */
export const ITEM_STATUS = {
  CONFIRMED: 'confirmed',
  CANCELLED: 'cancelled',
  REFUNDED: 'refunded',
} as const satisfies Record<string, ItemStatus>;

/** Book listing status */
export type BookStatus = 'active' | 'sold' | 'inactive' | 'pending_approval';

/** Book status constants */
export const BOOK_STATUS = {
  ACTIVE: 'active',
  SOLD: 'sold',
  INACTIVE: 'inactive',
  PENDING_APPROVAL: 'pending_approval',
} as const satisfies Record<string, BookStatus>;

/** Book condition values */
export type BookCondition = 'Like New' | 'Very Good' | 'Good' | 'Acceptable';

/** Book condition constants */
export const BOOK_CONDITION = {
  LIKE_NEW: 'Like New',
  VERY_GOOD: 'Very Good',
  GOOD: 'Good',
  ACCEPTABLE: 'Acceptable',
} as const satisfies Record<string, BookCondition>;
